import { useTranslation } from 'react-i18next';
import { ImageFormat, IntegrationSettings, MainImageVariant } from '@anticatrade/shared';
import SlRadio from '@shoelace-style/shoelace/dist/react/radio/index.js';
import SlRadioGroup from '@shoelace-style/shoelace/dist/react/radio-group/index.js';

interface WebsiteSettingsProps {
	settings: IntegrationSettings;
	onChange: (settings: IntegrationSettings) => void;
}

const WebsiteSettings = ({ settings, onChange }: WebsiteSettingsProps) => {
	const { t } = useTranslation();

	const imageFormatOptions: { value: ImageFormat; label: string; hint: string }[] = [
		{
			value: 'jpg',
			label: 'JPG',
			hint: t('Works on any website, but the item always sits on a white background.'),
		},
		{
			value: 'png',
			label: 'PNG',
			hint: t(
				'Keeps the cutout transparent, so the item blends into your page. Heavier photos.',
			),
		},
		{
			value: 'webp',
			label: 'WEBP',
			hint: t(
				"Keeps the cutout transparent, with lighter and sharper photos, but some websites don't accept this format.",
			),
		},
	];

	const mainImageVariantOptions: { value: MainImageVariant; label: string; hint: string }[] = [
		{
			value: 'cutout',
			label: t('Cutout'),
			hint: t('The item on its own, clean and uniform — like a product catalog.'),
		},
		{
			value: 'original-squared',
			label: t('Original photo, squared'),
			hint: t('Your original photo, background included, fitted into a square.'),
		},
		{
			value: 'original-natural',
			label: t('Original photo, full proportions'),
			hint: t('Your original photo exactly as you framed it, in its own proportions.'),
		},
	];

	return (
		<>
			<SlRadioGroup
				className='integration__option-group'
				label={t('Photo format')}
				value={settings.imageFormat ?? 'jpg'}
				onSlChange={(e: any) =>
					onChange({ ...settings, imageFormat: e.target.value as ImageFormat })
				}
			>
				{imageFormatOptions.map((option) => (
					<SlRadio key={option.value} value={option.value}>
						{option.label}
						<span className='integration__option-hint'>{option.hint}</span>
					</SlRadio>
				))}
			</SlRadioGroup>
			<SlRadioGroup
				className='integration__option-group'
				label={t('Main photo')}
				value={settings.mainImageVariant ?? 'cutout'}
				onSlChange={(e: any) =>
					onChange({ ...settings, mainImageVariant: e.target.value as MainImageVariant })
				}
			>
				{mainImageVariantOptions.map((option) => (
					<SlRadio key={option.value} value={option.value}>
						{option.label}
						<span className='integration__option-hint'>{option.hint}</span>
					</SlRadio>
				))}
			</SlRadioGroup>
		</>
	);
};

export { WebsiteSettings };
